import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBuildingWideWireless,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCableTv,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHighSpeedInternet,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSatelliteTv,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { AMENITY_SAVE_ERROR_MESSAGE } from '@zg-rentals/zillow-rental-manager/constants';

import { amenityPatches } from '../../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';

import {
  COMMUNICATION_KEYS,
  COMMUNICATION_METADATA,
  type CommunicationKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

import { type AmenityFlagSave } from '../../utils';

type LocalState = Record<CommunicationKey, boolean>;

const CommunicationAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const communicationTypes = listingDetails?.buildingAttributes?.communicationTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasBuildingWideWireless: communicationTypes?.hasBuildingWideWireless ?? false,
    hasSatelliteTv: communicationTypes?.hasSatelliteTv ?? false,
    hasCableTv: communicationTypes?.hasCableTv ?? false,
    hasHighSpeedInternet: communicationTypes?.hasHighSpeedInternet ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Internet/satellite updated',
  });

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.AMENITIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const { mutateAsync: updateBuildingWideWireless } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBuildingWideWireless,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBuildingWideWireless',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateSatelliteTv } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSatelliteTv,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSatelliteTv',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateCableTv } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCableTv,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: { api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCableTv', details: { listingAlias } },
    },
  });

  const { mutateAsync: updateHighSpeedInternet } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHighSpeedInternet,
    applyToCache: ({ postData }) => amenityPatches(postData),
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHighSpeedInternet',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasBuildingWideWireless: updateBuildingWideWireless,
    hasSatelliteTv: updateSatelliteTv,
    hasCableTv: updateCableTv,
    hasHighSpeedInternet: updateHighSpeedInternet,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as CommunicationKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = COMMUNICATION_METADATA[fieldName];
      if (isFeedListing) {
        await setFeedAmenity(metadata.apiKey, checked);
      } else {
        await mutationMap[fieldName]({
          postData: {
            [metadata.apiKey]: checked,
            listingAlias,
          },
        });
      }
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(err, AMENITY_SAVE_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Internet/satellite</Legend>} disabled={isInputFormDisabled}>
      {COMMUNICATION_KEYS.map((fieldName) => {
        const metadata = COMMUNICATION_METADATA[fieldName];
        return (
          <LabeledControl
            key={`communicationTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(CommunicationAmenities);
